package com.appcreator.blueprint.core

import androidx.compose.runtime.State
import com.appcreator.blueprint.core.ReadonlyEnvStore.Companion.create
import com.appcreator.blueprint.core.properties.BlueprintLink
import com.appcreator.blueprint.core.properties.LocalizableString
import io.ktor.http.encodeURLParameter
import kotlin.collections.contains

interface EnvStore {
    val defaultLocale: String
    var parent: EnvStore?
    val env: Map<String, Any>

    fun get(key: String, staticPass: Boolean = false): Any? {
        return if (key.startsWith("~")) {
            try {
                val parts = key.split(".")
                val first = parts.first()
                val end = parts.drop(1).joinToString(separator = ".")
                val parentCount = first.removePrefix("~").toInt()
                val parentKey = if (parentCount == 1) {
                    end
                } else {
                    "~${parentCount - 1}.$end"
                }
                parent?.get(parentKey, staticPass)
            } catch (ex: Exception) {
                null
            }
        } else if (env.contains(key)) {
            when (val value = env[key]) {
                is State<*> -> value.value
                is EnvFunction -> value.provide(this, staticPass)
                else -> value
            }
        } else {
            parent?.get(key, staticPass)?: println("$key Not found")
        }
    }

    fun injectLink(link: BlueprintLink): String {
        return injectValueWithMapping(link.path, link.params, emptyMap()) {
            it.encodeURLParameter()
        }
    }

    fun injectValueWithMapping(
        value: String,
        mapping: Map<String, String>,
        defaults: Map<String, String>,
        encoded: (String) -> String = { it }
    ): String {
        return injectVariables(value, false, encoded) { value, staticPass ->
            val mappedValue = mapping[value]?.let { mapped -> injectVariables(mapped) }
            val defaultValue = defaults[value]?.let { default -> injectVariables(default) }
            when {
                mappedValue?.isNotEmpty() == true -> mappedValue
                defaultValue?.isNotEmpty() == true -> defaultValue
                else -> get(value, staticPass)
            }
        }
    }

    /**
    staticPass is forwarded to any functions so they can give a constant result used in key generation not actual usage
     */
    fun injectVariables(
        value: String,
        staticPass: Boolean = false,
        encoded: (String) -> String = { it },
        valueForKey: (String, Boolean) -> Any? = { value, staticPass -> get(value, staticPass) },
    ): String {

        var updated = value
        envRegex.findAll(value).toList().reversed().forEach { match ->
            val key = match.value.trimStart('{').trimEnd('}')
            val replaceWith = valueForKey(key, staticPass)
            replaceWith?.let {
                updated = if (it is String) {
                    updated.replaceRange(match.range, encoded(it))
                } else {
                    // Remove property and replace with blank, can happen if a non string is provided as replacement
                    updated.replaceRange(match.range, "")
                }
            }?: run {
                // Remove property and replace with blank
                updated = updated.replaceRange(match.range, "")
            }
        }
        return updated
    }

    fun injectUrlSpec(urlSpec: UrlSpec, mapping: Map<String, String>, defaults: Map<String, String>): UrlSpec {
        val url = UrlSpec(
            url = injectValueWithMapping(urlSpec.url, mapping, defaults),
            verb = urlSpec.verb,
            queries = urlSpec.queries.map { (key, value) ->
                injectValueWithMapping(key, mapping, defaults) to injectValueWithMapping(value, mapping, defaults)
            }.toMap(),
            headers = urlSpec.headers.map { (key, value) ->
                injectValueWithMapping(key, mapping, defaults) to injectValueWithMapping(value, mapping, defaults)
            }.toMap(),
            bodyType = urlSpec.bodyType,
            body = injectValueWithMapping(urlSpec.body, mapping, defaults)
        )
        return url
    }

    fun envFromList(key: String, indexOffset: Int = 0): List<EnvStore> {
        (get(key) as? List<Map<String, Any>>)?.let {
            return it.mapIndexed { index, item ->
                create(this, env = item + mapOf("index" to (indexOffset + index).toString()))
            }
        }
        return emptyList()
    }

    fun injectLocalizableString(
        value: LocalizableString,
        locale: String?
    ): String {
        val localized = if (value.localizationEnabled) {
            value.values[locale ?: defaultLocale] ?: value.values[defaultLocale] ?: ""
        } else {
            value.values.values.firstOrNull() ?: ""
        }
        return injectVariables(localized)
    }

    fun listContains(listPath: String, item: Map<String, String>): Boolean {
        (get(listPath) as? List<Map<String, Any>>)?.let {
            return it.any { other -> item matches other }
        }
        return false
    }

    fun set(key: String, value: Any)
    fun add(key: String, value: Any)
    fun remove(key: String, toRemove: Map<String, String>)
}

class ReadonlyEnvStore private constructor(
    override val defaultLocale: String,
    override var parent: EnvStore?,
    override val env: Map<String, Any> = emptyMap()
): EnvStore {
    companion object {
        fun create(
            parent: EnvStore,
            env: Map<String, Any> = emptyMap()
        ) = ReadonlyEnvStore(
            defaultLocale = parent.defaultLocale,
            parent = parent,
            env = env
        )

        fun create(
            defaultLocale: String,
            env: Map<String, Any> = emptyMap(),
        ) = ReadonlyEnvStore(
            defaultLocale = defaultLocale,
            parent = null,
            env = env
        )
    }

    override fun equals(other: Any?): Boolean {
        return env == (other as? EnvStore)?.env
    }

    override fun set(key: String, value: Any) {
        parent?.set(key, value)
    }

    override fun add(key: String, value: Any) {
        parent?.add(key, value)
    }

    override fun remove(key: String, toRemove: Map<String, String>) {
       parent?.remove(key, toRemove)
    }
}

infix fun Map<String, String>.matches(other: Map<String, Any>): Boolean {
    return all { (key, value) ->
        value.isBlank() || other[key] == value
    }
}
